export const mean = (values: number[]): number => {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

export const stdDev = (values: number[]): number => {
  if (values.length < 2) return 0;
  const avg = mean(values);
  const variance = values.reduce((sum, value) => sum + (value - avg) ** 2, 0) / (values.length - 1);
  return Math.sqrt(variance);
};

export const quantile = (values: number[], q: number): number => {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const position = (sorted.length - 1) * q;
  const base = Math.floor(position);
  const rest = position - base;
  if (sorted[base + 1] !== undefined) {
    return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
  }
  return sorted[base];
};

export const iqrBounds = (values: number[]): { q1: number; q3: number; lower: number; upper: number } => {
  const q1 = quantile(values, 0.25);
  const q3 = quantile(values, 0.75);
  const iqr = q3 - q1;
  return {
    q1,
    q3,
    lower: q1 - 1.5 * iqr,
    upper: q3 + 1.5 * iqr
  };
};

export const pearson = (x: number[], y: number[]): number => {
  if (x.length !== y.length || x.length < 3) return 0;

  const xMean = mean(x);
  const yMean = mean(y);
  let numerator = 0;
  let xVariance = 0;
  let yVariance = 0;

  for (let i = 0; i < x.length; i += 1) {
    const dx = x[i] - xMean;
    const dy = y[i] - yMean;
    numerator += dx * dy;
    xVariance += dx * dx;
    yVariance += dy * dy;
  }

  const denominator = Math.sqrt(xVariance * yVariance);
  if (!denominator) return 0;
  return numerator / denominator;
};

export const linearRegression = (x: number[], y: number[]): { slope: number; intercept: number; r2: number } => {
  if (x.length !== y.length || x.length < 3) {
    return { slope: 0, intercept: 0, r2: 0 };
  }

  const xMean = mean(x);
  const yMean = mean(y);

  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < x.length; i += 1) {
    numerator += (x[i] - xMean) * (y[i] - yMean);
    denominator += (x[i] - xMean) ** 2;
  }

  if (!denominator) return { slope: 0, intercept: yMean, r2: 0 };

  const slope = numerator / denominator;
  const intercept = yMean - slope * xMean;

  let ssTotal = 0;
  let ssResidual = 0;
  for (let i = 0; i < x.length; i += 1) {
    const predicted = intercept + slope * x[i];
    ssTotal += (y[i] - yMean) ** 2;
    ssResidual += (y[i] - predicted) ** 2;
  }

  const r2 = ssTotal ? 1 - ssResidual / ssTotal : 0;
  return { slope, intercept, r2 };
};

export const parseNumericColumn = (rows: any[], column: string): number[] => (
  rows
    .map((row) => Number(row[column]))
    .filter((value) => Number.isFinite(value))
);

export const skewness = (values: number[]): number => {
  if (values.length < 3) return 0;
  const avg = mean(values);
  const sd = stdDev(values);
  if (sd === 0) return 0;
  const n = values.length;
  const sum = values.reduce((acc, value) => acc + ((value - avg) / sd) ** 3, 0);
  return (n / ((n - 1) * (n - 2))) * sum;
};
